import { IonContent, IonPage } from '@ionic/react';
import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { HiGlobe } from 'react-icons/hi';
import { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { routeTo } from '../../application/actions/ui';
import useLocale from '../../hooks/useLocale';
import ReDoExploreTab from '../../components/ReDoExploreTab';
import ReDoFooter from '../../components/ReDoFooter';
import logo from '../../assets/images/logo.jpg';


// Landing is the logged-out public page: a lightweight top menu (Register / Landing
// Page / Blog), the "Let's explore" browse experience in public mode, and the blog
// footer. Reached from useUrlRedirect when an anonymous visitor hits the app root.
const Landing = () => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const { t, language, changeLanguage } = useLocale();

  // The header can wrap to 2-3 lines on the narrowest phones (and with longer DE
  // strings), so measure its real height rather than assuming a single line — the
  // explore section fills exactly the rest of the first viewport. Falls back to a
  // sensible default if the ref hasn't attached yet.
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(90);
  useLayoutEffect(() => {
    const measure = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [language]);

  const menuItemClass = css({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 4px',
    fontSize: '15px',
    fontWeight: '600',
    color: theme.colors.black,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    ':hover': { opacity: '0.7' },
  });

  return (
    <IonPage>
      <IonContent>
        <Block display='flex' flexDirection='column' minHeight='100%' backgroundColor={theme.colors.white}>

          {/* Public top menu */}
          <Block
            ref={headerRef}
            as='header'
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            gridGap='1ch'
            padding={['1ch 2ch', '1ch 2ch', '1.5ch 3ch', '1.5ch 4ch']}
            overrides={{ Block: { style: { boxSizing: 'border-box', borderBottom: `1px solid ${theme.colors.secondary200}` } } }}
          >
            <Block display='flex' alignItems='center' gridGap='1ch'>
              <img src={logo} alt='ReDo' width='40' height='40' className={css({ borderRadius: '8px' })} />
              <span className={css({ fontWeight: '700', fontSize: '20px' })}>ReDo</span>
            </Block>

            <Block display='flex' alignItems='center' flexWrap='wrap' justifyContent='flex-end' gridGap={['1.25ch', '2ch', '3ch', '3ch']}>
              <button className={menuItemClass} onClick={() => dispatch(routeTo({ view: 'signup' }))}>
                {t('landing.menu_register')}
              </button>
              <a className={menuItemClass} href='https://landing.redo-please.com/' target='_blank' rel='noreferrer'>
                {t('landing.menu_landing')}
              </a>
              <a className={menuItemClass} href='https://blog.redo-please.com/' target='_blank' rel='noreferrer'>
                {t('landing.menu_blog')}
              </a>
              <button
                className={css({ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600', fontSize: '13px', letterSpacing: '0.03em' })}
                onClick={() => changeLanguage(language.startsWith('de') ? 'en' : 'de')}
              >
                <HiGlobe size={20} />
                {language.startsWith('de') ? 'DE' : 'EN'}
              </button>
            </Block>
          </Block>

          {/* Explore (public mode) — bounded height so the tab's own scroll engages */}
          <Block
            height={`calc(100vh - ${headerHeight}px)`}
            minHeight='0'
            display='flex'
            flexDirection='column'
          >
            <ReDoExploreTab isPublic />
          </Block>

          <ReDoFooter />

        </Block>
      </IonContent>
    </IonPage>
  );
};

export default Landing;
